import countries from './countries.json';

const isString = (input: any): input is string => typeof input === 'string' && input.length > 0;

const isNumber = (input: any): input is number => {
  if (Number.isNaN(input)) return false;
  if (typeof input === 'number') return true;
  if (typeof input === 'object') return false;
  if (typeof input === 'undefined') return false;
  return isString(input) && !Number.isNaN(Number.parseFloat(input));
};

const isNumberPositive = (input: unknown): input is number => isNumber(input) && input > 0;

const isValidGeoLocation = (input: object): boolean =>
  Object.values(input).every((v) => typeof v === 'number' && v !== 0);

const isMinimumChar = (input: any, min = 1): boolean => {
  const re = new RegExp(`^[ -~]{${min},}$`);
  return isString(input) && re.test(String(input).trim());
};
// test if the input is a valid country code from countries.json
const isCountryCode = (input: any, caseSensitive: boolean): boolean =>
  isString(input) &&
  countries.some((c) => {
    if (caseSensitive) return c.code === input;
    return c.code.toLowerCase() === input.toLowerCase();
  });

const isValidBackendString = (input: any): boolean => {
  const re = /^([a-z0-9~:._\-&=%@$#![\]]){1,}$/i;
  return typeof input === 'string' && re.test(input);
};

const isMaximumChar = (input: any, max = 1): boolean => {
  if (input.length === 0) return true;
  const re = new RegExp(`^[ -~]{${max},}$`);
  return isString(input) && re.test(String(input).trim());
};

const isEmail = (input: any): boolean => {
  const blacklist = [/privaterelay\.appleid\.com$/, /\.con$/, /@(gnail|gmeil)/];

  if (!isString(input)) return false;
  if (blacklist.some((tld) => tld.test(input))) return false;

  const re = /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return Boolean(String(input).trim().match(re));
};

const isEmailDomain = (input: string): boolean => {
  if (input.length > 255) return false;
  // Split domain into labels and validate each
  const labels = input.split('.');
  if (labels.length < 2) return false;

  if (labels.some((label) => label.length > 63 || !/^[a-zA-Z0-9-]+$/.test(label))) {
    return false;
  }

  // Ensure domain doesn't start or end with a hyphen
  if (labels.some((label) => label.startsWith('-') || label.endsWith('-'))) {
    return false;
  }
  return true;
};

const isEmailLocal = (input: string): boolean => {
  // Check length (max 64 characters)
  if (input.length > 64) return false;

  const unquoted = input.replace(/^"(.*)"$/, '$1');
  if (!/^[a-zA-Z0-9]/.test(unquoted)) return false;
  if (!/[a-zA-Z0-9]$/.test(unquoted)) return false;

  return /[a-zA-Z0-9.%+_-]/.test(unquoted);

  // Allow quoted local parts
  // const quotedRegex = /^"([^"]|\\")*"$/;
  // const unquotedRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;

  // return quotedRegex.test(input) || unquotedRegex.test(input);
};

const isEmailFn = (input: string): boolean => {
  const blacklist = [/privaterelay\.appleid\.com$/, /\.con$/, /@(gnail|gmeil)/];

  if (!isString(input)) return false;
  if (blacklist.some((tld) => tld.test(input))) return false;
  // Check basic structure with regex (to quickly catch invalid formats)
  // valid emails do NOT have spaces or more than one "@", and have at least one dot after the @ for the TLD
  const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!basicRegex.test(input)) return false; // Fail fast for obvious invalid cases

  const [local, domain] = input.split('@');
  if (!isEmailDomain(domain)) return false;
  if (!isEmailLocal(local)) return false;
  return true;
};

const isPassword = (input: any): boolean => isMinimumChar(input, 8);
const isPasswordConfirm = (a: any, b: any): boolean => isPassword(a) && isPassword(b) && a === b;

const isPhoneNumber = (input: any): boolean => {
  const regex = /^\+?([0-9\s]{5,15})$/;
  return isString(input) && Boolean(input.match(regex));
};
const isWebsiteAddress = (input: any): boolean => {
  const regex =
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;
  return isString(input) && Boolean(input.match(regex));
};

/** DO NOT USE THIS ON LARGE OBJECTS */
const isEmptyObject = (input: any): boolean => {
  if (!input) return false;
  if (typeof input !== 'object') return false;
  // const prototype = Object.getPrototypeOf(input);
  // if (!prototype !== Object.prototype) return false;
  // for (const prop in input) {
  //   if (Object.hasOwn(input, prop)) return false;
  // }
  return Object.keys(JSON.parse(JSON.stringify(input)))?.length === 0;
};

const isLink = (input: any): boolean => {
  const externals = /^(http(s)?:\/\/)[-a-zA-Z0-9@:%._+~#=]{2,}/;
  const internals = /^\/[-a-zA-Z0-9@:%._+~#=]{2,}/;
  return isString(input) && (externals.test(input) || internals.test(input));
};

// Strip only formatting separators (spaces and dashes),
// so "12-34 567" normalizes to "1234567".
// Any other character is preserved
const stripSeparators = (input: string): string => String(input).replace(/[\s-]/g, '');

const validateSnowitCard = (input: any): boolean => {
  if (!isString(input)) return false;
  // remove everything that is not a digit (e.g. spaces, dashes)
  const str = stripSeparators(input);
  // if length of input is not equal to 8, it's automatically not valid.
  // must be exactly 8 digits after removing separators.
  if (!/^[0-9]{8}$/.test(str)) return false;
  // legacy snowitcards. if they start with either 00, 01 or 02 they are automatically valid
  if (Number.parseInt(str.substring(0, 2), 10) < 3) {
    return true;
  }
  // the weight matrix
  const weight = [1, 7, 3, 1, 7, 3, 1];
  // split the input into an array of digits
  const number = str.split('').map(Number);
  // extract the last digit (the checksum)
  const checksum = number.pop();
  // dot product of the two matrices. modulus 10 to get the last digit;
  const dot = weight.reduce((a, b, i) => a + b * number[i], 0) % 10;
  // check if the above is equal to checksum
  return checksum === dot;
};

const validateSkiLombardiaCard = (input: any): boolean => {
  if (!isString(input)) return false;
  const str = stripSeparators(input);
  // must be exactly 7 digits
  return /^[0-9]{7}$/.test(str);
};

const validateAxessCard = (input: any): boolean => {
  if (!isString(input)) return false;
  const str = input.replace(/\s/g, '');
  // must be exactly 14 alphanumeric characters (8-3-3);
  return /^[A-Z0-9]{8}-[A-Z0-9]{3}-[A-Z0-9]{3}$/.test(str);
};

const validateSkidataCard = (input: any, zeroOne = false): boolean => {
  if (!isString(input)) return false;
  const str = stripSeparators(input);
  // must be exactly 23 digits after removing separators.
  if (!/^[0-9]{23}$/.test(str)) return false;
  const STARTS_WITH = ['01', '29', '30'];
  const startsWith = STARTS_WITH.some((start) => str.startsWith(start));
  if (zeroOne && !startsWith) return false;
  const parity = str.length % 2;
  const sum = str
    .split('')
    .map(Number)
    .reverse()
    .reduce((a, b, i) => {
      let d = b * (i % 2 === parity ? 2 : 1);
      if (d > 9) d -= 9;
      return a + d;
    }, 0);
  return sum % 10 === 0;
};

const validateSkicardLombardia = (input: any): boolean => {
  if (!isString(input)) return false;
  // remove everything that is not a digit.
  const str = String(input).replace(/\D/g, '');
  // must be exactly 0 digits after removing separators.
  return /^[0-9]{20}$/.test(str);
};

const isSnowitcard = (input: any, zeroOne?: boolean): boolean =>
  // validateSkiLombardiaCard(input) ||
  validateSnowitCard(input) || validateSkidataCard(input, zeroOne) || validateAxessCard(input);

const isSkidatacard = (
  input: any,
  params: {
    zeroOne?: boolean;
    lombardia?: boolean;
    skidata?: boolean;
    axess?: boolean;
  } = {},
) => {
  const { zeroOne = false, lombardia = false, skidata = true, axess = false } = params;
  return [
    lombardia ? validateSkicardLombardia(input) : undefined,
    axess ? validateAxessCard(input) : undefined,
    skidata ? validateSkidataCard(input, zeroOne) : undefined,
  ]
    .filter((c) => typeof c !== 'undefined')
    .some(Boolean);
};

const isHtml = (input: any): boolean =>
  isString(input) && typeof input === 'string' && Boolean(input.match(/^</));

const isNull = (input: any): boolean => !input && !Number.isFinite(Number.parseFloat(input));

const isReferral = (input: any): boolean =>
  isString(input) && isMinimumChar(input, 6) && isMaximumChar(input, 6);

const isCodiceFiscale = (cf: string): boolean => {
  if (!cf) return false;
  const CF = cf.replace(/[\s\W]/g, '').toUpperCase();
  // cf pattern is e.g. AAAAAA00A00A000A
  const cfReg = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/;

  if (!cfReg.test(CF)) return false;
  const set1 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const set2 = 'ABCDEFGHIJABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const setpari = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const setdisp = 'BAKPLCQDREVOSFTGUHMINJWZYX';
  const A = 'A'.charCodeAt(0);
  let s = 0;
  for (let i = 1; i <= 13; i += 2) {
    s += setpari.indexOf(set2.charAt(set1.indexOf(CF.charAt(i))));
  }
  for (let i = 0; i <= 14; i += 2) {
    s += setdisp.indexOf(set2.charAt(set1.indexOf(CF.charAt(i))));
  }
  if (s % 26 !== CF.charCodeAt(15) - A) return false;
  return true;
};

const isPartitaIva = (piva: string): boolean => {
  if (!piva) return false;
  const PIVA = piva.replace(/[\s\W]/g, '').toUpperCase();
  // allow partita iva to be written with leading "IT"
  // in any case it must have 11 digits
  const pivaReg = /^(IT)?\d{11}/;
  if (!pivaReg.test(PIVA)) return false;
  // remove any non digit, if any
  const zero = '0'.charCodeAt(0); // 48
  const pi = PIVA.replace(/\D/g, '');
  let s = 0;
  let c = 0;
  for (let i = 0; i <= 9; i += 2) {
    s += pi.charCodeAt(i) - zero;
  }
  for (let i = 1; i <= 9; i += 2) {
    c = 2 * (pi.charCodeAt(i) - zero);
    if (c > 9) c = c - 9;
    s += c;
  }

  if ((10 - (s % 10)) % 10 !== pi.charCodeAt(10) - zero) {
    return false;
  }
  return true;
};

const isGenericVat = (vat: string): boolean => {
  if (!vat) return false;
  const VAT = vat.replace(/[\s\W]/g, '').toUpperCase();
  const vatReg = /^[A-Z]{2}\w+/;
  if (!vatReg.test(VAT)) return false;
  return true;
};

const isURL = (s?: string): boolean => {
  if (!s) return false;
  try {
    return Boolean(new URL(s));
  } catch {
    return false;
  }
};

const isFisiNumber = (input: any): boolean => {
  if (!isString(input)) return false;
  if (!input) return false;
  return /^[a-zA-Z0-9]+$/.test(input);
};

const isMaestriNumber = (input: any): boolean => {
  if (!isString(input)) return false;
  if (!input) return false;
  // remove every non-alphanumeric character
  // const str = String(input)
  //   .replace(/[^a-zA-Z0-9]/g, '')
  //   .toUpperCase();
  // return /^[A-Z]\d{4}$/.test(input) || /^IT\d{6}$/.test(input);
  return /^[a-zA-Z0-9]+$/.test(input);
};

const isValidDomain = (input: any): boolean => {
  // domain must be a string
  if (!isString(input)) return false;
  // must not be empty
  if (!input) return false;
  // must contain at least one dot (e.g. "example" is not a valid domain)
  if (!/\./.test(input)) return false;
  // must not contain anything but letters, numbers, dash and dots
  if (/[^a-z0-9-.]/.test(input)) return false;
  // must start and end with a letter/number
  if (!/^[a-z0-9].*[a-z0-9]$/.test(input)) return false;
  return true;
};

const isValidURL = (input: any): boolean => {
  if (!isString(input)) return false;
  if (!input) return false;
  return /^https?:\/\/[^\s/$.?#].[^\s]*$/.test(input);
};

const isValidInternalURL = (input: unknown): boolean => {
  if (!isString(input)) return false;
  if (!input) return false;
  return /^\/[^\s.]*$/.test(input);
};

const isValidSlug = (input: any): boolean => {
  if (!isString(input)) return false;
  if (!input) return false;
  return /^[a-z0-9].*[a-z0-9]$/.test(input);
};

export default {
  isCodiceFiscale,
  isCountryCode,
  isEmail,
  isEmailFn,
  isGenericVat,
  isHtml,
  isLink,
  isMaximumChar,
  isMinimumChar,
  isNull,
  isNumber,
  isNumberPositive,
  isPartitaIva,
  isPassword,
  isPasswordConfirm,
  isPhoneNumber,
  isReferral,
  isSkidatacard,
  isSnowitcard,
  isString,
  isValidBackendString,
  isValidGeoLocation,
  isWebsiteAddress,
  isURL,
  isMaestriNumber,
  isFisiNumber,
  validateSkidataCard,
  validateSnowitCard,
  validateSkiLombardiaCard,
  validateAxessCard,
  isEmptyObject,
  isValidDomain,
  isValidURL,
  isValidInternalURL,
  isValidSlug,
};
